import { axiosPrivate as axiosInstance } from "../../../api/axios";

const projectPath = (groupCode, projectCode) =>
  `/risk/${encodeURIComponent(groupCode)}/${encodeURIComponent(projectCode)}`;

const asArray = (value) => (Array.isArray(value) ? value : []);

export const getSafetyOverview = async ({ groupCode, projectCode, date }) => {
  const basePath = projectPath(groupCode, projectCode);
  const currentDate = date instanceof Date ? date : new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;
  const evalDate = [
    year,
    String(month).padStart(2, "0"),
    String(currentDate.getDate()).padStart(2, "0"),
  ].join("-");

  const requests = await Promise.allSettled([
    axiosInstance.get(`${basePath}/wizard/pending-hazards`, {
      params: { eval_date: evalDate },
    }),
    axiosInstance.get(`${basePath}/getRiskAssessmentApproval`),
    axiosInstance.get(`${basePath}/wizard/step-items`, {
      params: { eval_date: evalDate },
    }),
  ]);

  const value = (index, fallback) =>
    requests[index].status === "fulfilled"
      ? requests[index].value.data
      : fallback;

  const pending = value(0, {});
  const saved = value(2, {});
  const workflowAvailable = Array.isArray(pending?.rows) && Array.isArray(saved?.rows);
  const highRiskAvailable = Array.isArray(value(1, null));
  return {
    pendingHazards: asArray(pending?.rows),
    evaluations: asArray(value(1, [])),
    stepItems: asArray(saved?.rows),
    cycle: saved?.cycle || pending?.cycle || null,
    workflowAvailable,
    highRiskAvailable,
    hasError: !workflowAvailable || !highRiskAvailable,
  };
};

export const getUpcomingSafetyEvents = async ({
  groupCode,
  projectCode,
  start,
  end,
}) => {
  const dayMs = 86400000;
  const from = Date.parse(`${start}T00:00:00Z`);
  const to = Date.parse(`${end}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to) || from > to) {
    throw new Error("Invalid safety calendar range");
  }
  // The calendar API caps each response at 62 days; DT needs both past and future dates.
  const ranges = [];
  for (let cursor = from; cursor <= to; cursor += 62 * dayMs) {
    ranges.push({
      start: new Date(cursor).toISOString().slice(0, 10),
      end: new Date(Math.min(to, cursor + 61 * dayMs)).toISOString().slice(0, 10),
      evaluation_date: "due",
    });
  }
  const pages = await Promise.all(ranges.map(async (params) => {
    const response = await axiosInstance.get(
      `${projectPath(groupCode, projectCode)}/calendar`, { params }
    );
    const data = response?.data;
    return data && typeof data === "object" ? data : {};
  }));
  const lists = {};
  const summary = {};
  pages.forEach((page) => {
    Object.entries(page.lists || {}).forEach(([key, rows]) => {
      lists[key] = [...(lists[key] || []), ...asArray(rows)];
    });
    Object.entries(page.summary || {}).forEach(([key, count]) => {
      summary[key] = (summary[key] || 0) + (Number(count) || 0);
    });
  });
  return {
    ...pages[0], start, end, lists, summary,
    events: pages.flatMap((page) => asArray(page.events)),
    days: pages.flatMap((page) => asArray(page.days)),
  };
};
